import React, { useState } from "react";
import { useLang } from "../../../i18n/LanguageContext";
import { useAuth } from "../../../auth/AuthContext";

const copyForLang = (lang) => ({
  vaultNotice: lang === "en"
    ? "You are setting one Morfeusz password. It will open your account and private dreams. It cannot be recovered, so keep it somewhere safe."
    : "Ustawiasz jedno hasło Morfeusza. Będzie otwierać konto i prywatne sny. Nie da się go odzyskać, dlatego zapisz je w bezpiecznym miejscu.",
  passwordHint: lang === "en"
    ? "Minimum 6 characters. You may use a longer password if you wish."
    : "Minimum 6 znaków. Możesz użyć dłuższego hasła, jeśli chcesz.",
  passwordTooShort: lang === "en" ? "Password must be at least 6 characters." : "Hasło powinno mieć minimum 6 znaków.",
  activationNotice: lang === "en"
    ? "Your account has been created. Check your email and activate it."
    : "Konto zostało utworzone. Sprawdź e-mail i aktywuj konto.",
  eyebrow: lang === "en" ? "Dream gate" : "Senna brama",
  registerTitlePre: lang === "en" ? "Open the" : "Otwórz",
  registerTitleAccent: lang === "en" ? "Dream Codex" : "Księgę Snów",
  email: "Email",
  displayName: lang === "en" ? "Handle" : "Ksywka",
  displayNamePlaceholder: lang === "en" ? "Dreamer" : "Śniący",
  displayNameHint: lang === "en" ? "You may leave it blank — we will derive one from your email." : "Możesz zostawić puste — uzupełnimy z emaila.",
  password: lang === "en" ? "Password" : "Hasło",
  passwordConfirm: lang === "en" ? "Confirm password" : "Powtórz hasło",
  submitRegister: lang === "en" ? "Create account" : "Załóż konto",
  haveAccount: lang === "en" ? "Already have an account?" : "Masz już konto?",
  signInLink: lang === "en" ? "Sign in" : "Zaloguj się",
});

export const RegisterPage = () => {
  const { lang, t } = useLang();
  const { register, authMode } = useAuth();
  const copy = t.auth || {};
  const errs = (copy && copy.errors) || {};
  const localCopy = copyForLang(lang);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (!email.trim()) return setError(errs.required || "Required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return setError(errs.invalidEmail || "Invalid email");
    }
    if (!password || password.length < 6) return setError(localCopy.passwordTooShort);
    if (password !== confirm) return setError(errs.passwordMismatch || "Mismatch");
    setSubmitting(true);
    try {
      const result = await register(email, password, displayName);
      if (result?.requiresActivation) {
        setNotice(result.message || localCopy.activationNotice);
        setPassword("");
        setConfirm("");
        return;
      }
      window.location.hash = "#/profil";
    } catch (err) {
      if (err.code === "email_taken" || err.code === "email-taken") setError(errs.emailTaken || "Email taken");
      else if (err.code === "invalid_email") setError(errs.invalidEmail || "Invalid email");
      else if (err.code === "password_too_short") setError(errs.passwordTooShort || "Too short");
      else setError(err.message || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mdPage" data-testid="register-page">
      <div className="mdPage__inner">
        <div>
          <p className="mdPage__eyebrow">{copy.eyebrow || localCopy.eyebrow}</p>
          <h1 className="mdPage__title">
            {copy.registerTitlePre || localCopy.registerTitlePre} <em>{copy.registerTitleAccent || localCopy.registerTitleAccent}</em>
          </h1>
          <p className="mdPage__subtitle">{copy.registerSubtitle}</p>
        </div>

        <form className="mdCard" onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="mdAlert" data-testid="register-error">
              {error}
            </div>
          )}

          {notice && (
            <div className="mdAlert mdAlert--info" data-testid="register-notice">
              {notice}
            </div>
          )}

          <div className="mdAlert mdAlert--info" data-ui="register-vault-password-notice">
            {localCopy.vaultNotice}
          </div>

          <div className="mdField">
            <label className="mdField__label" htmlFor="reg-email">{copy.email || localCopy.email}</label>
            <input
              id="reg-email"
              type="email"
              autoComplete="email"
              className="mdField__input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email"
              required
            />
          </div>

          <div className="mdField">
            <label className="mdField__label" htmlFor="reg-name">{copy.displayName || localCopy.displayName}</label>
            <input
              id="reg-name"
              type="text"
              className="mdField__input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="register-name"
              placeholder={copy.displayNamePlaceholder || localCopy.displayNamePlaceholder}
            />
            <span className="mdField__hint">{copy.displayNameHint || localCopy.displayNameHint}</span>
          </div>

          <div className="mdField">
            <label className="mdField__label" htmlFor="reg-pass">{copy.password || localCopy.password}</label>
            <input
              id="reg-pass"
              type="password"
              autoComplete="new-password"
              className="mdField__input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="register-password"
              required
              minLength={6}
            />
            <span className="mdField__hint">{localCopy.passwordHint}</span>
          </div>

          <div className="mdField">
            <label className="mdField__label" htmlFor="reg-pass2">{copy.passwordConfirm || localCopy.passwordConfirm}</label>
            <input
              id="reg-pass2"
              type="password"
              autoComplete="new-password"
              className="mdField__input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="register-password-confirm"
              required
              minLength={6}
            />
          </div>

          <button
            type="submit"
            className="mdBtn mdBtn--primary"
            disabled={submitting}
            data-testid="register-submit"
          >
            {submitting ? (copy.loading || "…") : (copy.submitRegister || localCopy.submitRegister)}
          </button>

          <p className="mdMuted" style={{ marginTop: 18 }}>
            {copy.haveAccount || localCopy.haveAccount}{" "}
            <a href="#/zaloguj" className="mdLink" data-testid="link-login">
              {copy.signInLink || localCopy.signInLink}
            </a>
          </p>

          {authMode === "mock" && (
            <div className="mdAlert mdAlert--info" style={{ marginTop: 16 }}>
              {copy.mockNotice}
            </div>
          )}
        </form>
      </div>
    </section>
  );
};
